import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { OverlayTrigger, Popover } from "react-bootstrap";
import { LoadingScreen } from "Components/LoadingScreen";
import { UserHover } from "Components/UserHover";
import { authService, newAuthService } from "Service/Axios";
import {
  APPLICATION_DOCUMENTS_URL,
  APPLICATION_FILE_DELETE_URL,
  // APPLICATION_FILE_RENAME_URL,
  APPLICATION_FILE_UPLOAD_URL,
  GET_APPLICANT_OVERVIEW_DOCUMENTS_URL,
  UPDATE_APPLICATION_FILE_NAME_URL,
  UPDATE_APPLICATION_FILE_URL,
} from "Constant/api";
import { useOutsideClick } from "Utils/hooks";
import {
  // downloadFile,
  formatDateWithShortMonth,
  getFirstLetterOfName,
} from "Utils";

export const Document = ({ gid, is360View }) => {
  const { t } = useTranslation();

  const documentRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [documentList, setDocumentList] = useState([]);
  const [duplicateDocumentList, setDuplicateDocumentList] = useState([]);

  const getApplicationDocuments = async () => {
    setLoading(true);
    const payload = {};
    if (is360View) {
      payload.userId = gid;
    } else {
      payload.gid = gid;
    }
    const response = await newAuthService(
      is360View
        ? GET_APPLICANT_OVERVIEW_DOCUMENTS_URL
        : APPLICATION_DOCUMENTS_URL,
      "POST",
      payload
    );
    if (response.status && response.data && response.data.length > 0) {
      setDocumentList(response.data[0].fieldDetails);
      setDuplicateDocumentList(JSON.stringify(response.data[0].fieldDetails));
      setLoading(false);
    } else {
      setDocumentList([]);
      setLoading(false);
    }
  };

  useEffect(() => {
    getApplicationDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gid]);

  // On Change
  const onCreateUploadFile = async (e) => {
    const file = e.target.files[0];
    const formData = new FormData();
    formData.append("file", file);
    formData.append("gid", gid);
    await newAuthService(APPLICATION_FILE_UPLOAD_URL, "POST", formData);
    getApplicationDocuments();
  };

  const onDragUploadFile = (e) => {
    e.preventDefault();
  };

  const onDropUploadFile = (e) => {
    e.preventDefault();
    const droppedFiles = e.dataTransfer.files;
    if (droppedFiles.length > 0) {
      const mockEvent = {
        target: {
          files: droppedFiles,
        },
      };
      onCreateUploadFile(mockEvent);
    }
  };

  const onEditUploadFile = async (e, questionId) => {
    const file = e.target.files[0];
    const formData = new FormData();
    formData.append("file", file);
    formData.append("taskId", gid);
    formData.append("appType", "");
    if (questionId) {
      formData.append("gid", questionId);
    }
    await newAuthService(UPDATE_APPLICATION_FILE_URL, "POST", formData);
    getApplicationDocuments();
  };

  const handleChangeDocument = (e, index) => {
    const newArr = [...documentList];
    newArr[index].name = e.target.value;
    setDocumentList(newArr);
  };

  // Key Down
  const handleKeyPress = async (e, document) => {
    if (e.key === "Enter") {
      const payload = {
        taskId: gid,
        gid: document.questionId,
        name: e.target.value,
      };
      await authService(UPDATE_APPLICATION_FILE_NAME_URL, "POST", payload);
      getApplicationDocuments();
    }
  };

  // Handle
  const onViewDocument = (url) => {
    // const response = await axios.get(url, {
    //   responseType: 'arraybuffer',
    // });

    // const blob = new Blob([response.data], { type: 'application/pdf' });
    // const objectUrl = URL.createObjectURL(blob);
    // window.open(objectUrl, '_blank');
    window.open(url, "_blank");
  };

  const onEditDocument = (index) => {
    const newArr = documentList.map((col, documentIndex) =>
      documentIndex === index
        ? {
            ...col,
            isEditable: true,
          }
        : {
            ...col,
            isEditable: false,
          }
    );
    setDocumentList(newArr);
  };

  const onDeleteDocument = async (fileName, questionId) => {
    const data = {
      taskId: gid,
      gid: questionId,
      name: fileName,
    };
    await authService(APPLICATION_FILE_DELETE_URL, "POST", data);
    getApplicationDocuments();
  };

  const onDownLoadDocument = (url) => {
    if (url) {
      // Use a regular expression to match the filename after the last '/'
      const matches = url.match(/\/([^\/]+)$/);

      if (matches && matches.length > 1) {
        const filenameWithExtension = matches[1];
        // Output: Changes.docx

        // If you want to separate the filename and extension:
        // const [filename, extension] = filenameWithExtension.split(".");

        fetch(url)
          .then((response) => response.blob())
          .then((blob) => {
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = filenameWithExtension;
            link.click();
            URL.revokeObjectURL(url);
          });
      } else {
        toast.error("Filename not found in URL.");
      }
    }
  };

  const onCancelDocument = () => {
    setDocumentList(JSON.parse(duplicateDocumentList));
  };

  const disableDocumentEdit = () => {
    const newArr = documentList.map((docs) => ({
      ...docs,
      makeEditable: false,
    }));
    setDocumentList(newArr);
  };
  useOutsideClick(documentRef, () => disableDocumentEdit());

  return (
    <div className="applicant-status-body timeline-tab px-5 py-3 bg-white">
      <div className="document-body">
        <div
          className="mb-2 file-input-box bg-white border-dashed border-2 border-slate-300 rounded"
          onDrop={onDropUploadFile}
          onDragOver={onDragUploadFile}
        >
          <label htmlFor="file" className="d-block p-5 text-center bg-slate-50">
            <span className="material-symbols-outlined">cloud_upload</span>
            <h5 className="mb-0 text-base font-medium text-slate-600">
              Drop your document here
              <br />
              or Click to browse
            </h5>
            <small>File Size 20 MB</small>
          </label>
          <input
            type="file"
            id="file"
            className="d-none"
            onChange={onCreateUploadFile}
          />
        </div>

        <div className="d-flex align-items-center mb-2">
          <h3 className="mb-0 me-3 text-md font-semibold text-grey-800">
            Attached documents
          </h3>
          <ul className="filter-list ms-auto">
            <li>
              <button type="button" className="button badge-btn">
                <i className="pluto-filter"></i> Filters
              </button>
            </li>
          </ul>
        </div>
        <div className="table-container max-h-350 overflow-auto">
          <table className="table data-table">
            <thead className="table-light">
              <tr>
                <th>Document Name</th>
                <th>Document Type</th>
                <th>Date Uploaded</th>
                <th>File Size</th>
                <th>Uploaded By</th>
                <th width={200} className="text-center">
                  Action
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr className="loading-screen">
                  <td colSpan={6}>
                    <LoadingScreen />
                  </td>
                </tr>
              ) : documentList?.length > 0 ? (
                documentList.map((document, index) => (
                  <tr key={index.toString()}>
                    <td>
                      <div className="form-check">
                        <input
                          className="form-check-input me-1"
                          type="checkbox"
                          value=""
                          id=""
                        />
                        {document.isEditable ? (
                          <input
                            className="form-control form-control-sm"
                            type="text"
                            value={document.name}
                            onChange={(e) => handleChangeDocument(e, index)}
                            onKeyDown={(e) => handleKeyPress(e, document)}
                          />
                        ) : (
                          document.name
                        )}
                      </div>
                    </td>
                    <td>
                      <span className="d-inline-flex px-3 py-2 fw-semibold text-light-emphasis bg-light-subtle border border-success-subtle rounded-1">
                        {document.type}
                      </span>
                    </td>
                    <td>
                      {document.uploadedDate &&
                        formatDateWithShortMonth(document.uploadedDate)}
                    </td>
                    <td>{document.fileSize}</td>
                    <td>
                      {document.userDetails && (
                        <div className="">
                          {/* <div className="d-flex align-items-center column-gap-1 cursor-pointer">
                            <div className="img-box rounded-circle overflow-hidden me-1">
                              {document.userDetails.photoURL ? (
                                <img
                                  src={document.userDetails.photoURL}
                                  alt={`${document.userDetails.firstName}${document.userDetails.lastName}`}
                                  className="w-100 h-100 object-fit-cover"
                                />
                              ) : (
                                getFirstLetterOfName(document.userDetails)
                              )}
                            </div>
                            <h6 className="mb-0 text-xs">
                              {document.userDetails.firstName}{" "}
                              {document.userDetails.lastName}
                            </h6>
                          </div> */}

                          {/* <div className="applicant-info-box top-100 start-0 card-shadow p-3">
                            <div className="application-info-box top-0 z-10 translate-y-0">
                              <div className="d-flex align-items-center pb-3">
                                <div className="application-img relative me-2">
                                  <div className="status status-online"></div>
                                  {document.userDetails.photoURL ? (
                                    <div className="img-box w-100 h-100">
                                      <img
                                        src={document.userDetails.photoURL}
                                        alt={`${document.userDetails.firstName}${document.userDetails.lastName}`}
                                        className="w-100 h-100 object-fit-cover"
                                      />
                                    </div>
                                  ) : (
                                    getFirstLetterOfName(document.userDetails)
                                  )}
                                </div>
                                <div className="application-info">
                                  <small>
                                    #{document.userDetails.applicantId}
                                  </small>
                                  <h6>
                                    {document.userDetails.firstName}{" "}
                                    {document.userDetails.lastName}
                                  </h6>
                                </div>
                              </div>
                              <div className="application-applicant-details">
                                <ul>
                                  <li>
                                    {t("common.label.email")}:{" "}
                                    <strong>
                                      {document.userDetails.email}
                                    </strong>
                                  </li>
                                  <li>
                                    {t("common.label.contactNo")}:{" "}
                                    <strong>
                                      {document.userDetails.contactNo}
                                    </strong>
                                  </li>
                                  <li>
                                    {t("common.label.degree")}:{" "}
                                    <strong>
                                      {document.userDetails.degree}
                                    </strong>
                                  </li>
                                  <li>
                                    {t("common.label.university")}:{" "}
                                    <strong>
                                      {document.userDetails.university}
                                    </strong>
                                  </li>
                                </ul>
                              </div>
                              <div className="d-grid gap-1">
                                <button
                                  type="button"
                                  className="btn btn-mini btn-block btn-light"
                                >
                                  {t("common.button.viewProfile")}
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-mini btn-block btn-primary"
                                >
                                  {t("common.button.sendMessage")}
                                </button>
                              </div>
                            </div>
                          </div> */}

                          {/* <UserHover user={document.userDetails} /> */}

                          <OverlayTrigger
                            placement="top"
                            overlay={
                              <Popover className="max-w-unset">
                                <UserHover user={document.userDetails} />
                              </Popover>
                            }
                          >
                            <div className="d-flex align-items-center cursor-pointer">
                              <div className="w-8 h-8 rounded-circle overflow-hidden">
                                {document.userDetails.photoURL ? (
                                  <img
                                    src={document.userDetails.photoURL}
                                    alt={`${document.userDetails.firstName}${document.userDetails.lastName}`}
                                    className="w-100 h-100 object-fit-cover"
                                  />
                                ) : (
                                  getFirstLetterOfName(document.userDetails)
                                )}
                              </div>

                              <div className="avatar-short-dtls ms-2">
                                <h6 className="mb-0 text-xs">
                                  {document.userDetails.firstName}{" "}
                                  {document.userDetails.lastName}
                                </h6>
                              </div>
                            </div>
                          </OverlayTrigger>
                        </div>
                      )}
                    </td>
                    <td>
                      <div className="d-flex justify-content-center align-items-center gap-1">
                        <button
                          type="button"
                          className="d-flex justify-content-center align-items-center w-8 h-8 p-2 text-sm bg-transparent border border-solid border-grey-300 rounded-2"
                          onClick={() => onViewDocument(document.value)}
                        >
                          <i className="bx bx-show bx-xs text-purple-600"></i>
                        </button>
                        {!document.isEditable ? (
                          <button
                            type="button"
                            className="d-flex justify-content-center align-items-center w-8 h-8 p-2 text-sm bg-transparent border border-solid border-grey-300 rounded-2"
                            onClick={() => onEditDocument(index)}
                          >
                            <i className="bx bx-pencil bx-xs text-green-800"></i>
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="d-flex justify-content-center align-items-center w-8 h-8 p-2 text-sm bg-transparent border border-solid border-grey-300 rounded-2"
                            onClick={() => onCancelDocument()}
                          >
                            <i className="bx bx-undo bx-xs text-orange-400"></i>
                          </button>
                        )}
                        <span className="upload-btn-wrapper">
                          <button
                            type="button"
                            className="d-flex justify-content-center align-items-center w-8 h-8 p-2 text-sm bg-transparent border border-solid border-grey-300 rounded-2 cursor-pointer"
                          >
                            <input
                              type="file"
                              onChange={(e) =>
                                onEditUploadFile(e, document.questionId)
                              }
                            />
                            <i className="bx bx-upload bx-xs text-purple-900"></i>
                          </button>
                        </span>
                        {/* <button
                          type="button"
                          className="d-flex justify-content-center align-items-center w-8 h-8 p-2 text-sm bg-transparent border border-solid border-grey-300 rounded-2"
                          onClick={() =>
                            onDownLoadDocument(document.value, document.name)
                          }
                        >
                          <i className="bx bx-download bx-xs text-orange-400"></i>
                        </button> */}
                        <button
                          type="button"
                          className="d-flex justify-content-center align-items-center w-8 h-8 p-2 text-sm bg-transparent border border-solid border-grey-300 rounded-2"
                          onClick={() =>
                            onDeleteDocument(document.name, document.questionId)
                          }
                        >
                          <i className="bx bx-trash bx-xs text-red-600"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr className="loading-screen">
                  <td colSpan={6}>
                    <div className="text-center">
                      <h5 className="mb-0 text-base font-medium text-slate-600">
                        {t("common.messages.error.notFound.noDocumentsFound")}
                      </h5>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
